import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { CheckCircle2, ClipboardList, Loader2, Target, UserCircle } from "lucide-react";
import { Heart, Brain, Ribbon, Droplet } from "lucide-react";

const SPECIALTY_META = {
  cardiology: { icon: Heart,   accent: "#F87171", label: "Cardiology" },
  neurology:  { icon: Brain,   accent: "#A78BFA", label: "Neurology" },
  oncology:   { icon: Ribbon,  accent: "#FBBF24", label: "Oncology" },
  nephrology: { icon: Droplet, accent: "#4ADE80", label: "Nephrology" },
};

/**
 * CarePlansPanel — Phase 2.
 * Per-specialty care plan templates (patient goals + provider tasks +
 * review cadence). Read-only reference. Used on `/care-planning` page.
 */
export default function CarePlansPanel({ defaultSpecialty = "nephrology" }) {
  const [specialty, setSpecialty] = useState(defaultSpecialty);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const meta = SPECIALTY_META[specialty] || SPECIALTY_META.nephrology;

  useEffect(() => {
    setLoading(true); setErr("");
    (async () => {
      try {
        const { data } = await api.get(`/platform/care-plans/${specialty}`);
        setPlans(data.plans || []);
      } catch (e) {
        setErr(e?.response?.data?.detail || "Failed to load care plans");
        setPlans([]);
      } finally { setLoading(false); }
    })();
  }, [specialty]);

  const TABS = ["nephrology", "cardiology", "neurology", "oncology"];

  return (
    <div className="space-y-4" data-testid="care-plans-panel">
      <div className="ros-card">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-2xl flex items-center justify-center"
               style={{ background: `${meta.accent}22`, border: `1px solid ${meta.accent}55` }}>
            <ClipboardList size={22} style={{ color: meta.accent }} />
          </div>
          <div>
            <h2 className="font-heading text-2xl">Care planning</h2>
            <p className="text-sm text-[#A3ACA7] mt-0.5">
              Guideline-aligned care templates — patient goals, provider tasks, and review cadence per specialty.
            </p>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap gap-2" data-testid="care-plans-tabs">
        {TABS.map((s) => {
          const m = SPECIALTY_META[s];
          const Icon = m.icon;
          const active = specialty === s;
          return (
            <button key={s} onClick={() => setSpecialty(s)}
                    data-testid={`care-plans-tab-${s}`}
                    className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold border transition-colors ${
                      active ? "text-black border-transparent"
                             : "bg-[#0F1312] text-[#A3ACA7] border-[#272E2B] hover:text-white"
                    }`}
                    style={active ? { background: m.accent } : {}}>
              <Icon size={14} /> {m.label}
            </button>
          );
        })}
      </div>

      {loading ? (
        <div className="text-center text-[#A3ACA7] py-16 flex items-center justify-center gap-2">
          <Loader2 size={16} className="animate-spin" /> Loading care plans…
        </div>
      ) : err ? (
        <div className="ros-card text-sm text-[#F87171]" data-testid="care-plans-error">{err}</div>
      ) : (
        <div className="space-y-4">
          {plans.map((p) => (
            <div key={p.id} className="ros-card" data-testid={`care-plan-${p.id}`}
                 style={{ borderLeftWidth: 2, borderLeftColor: meta.accent }}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="font-heading text-xl text-white">{p.title}</div>
                  <div className="text-xs text-[#A3ACA7] mt-1">{p.stage}</div>
                </div>
                <span className="text-[10px] uppercase tracking-wider px-2.5 py-1 rounded-full whitespace-nowrap"
                      style={{ color: meta.accent, background: `${meta.accent}18`, border: `1px solid ${meta.accent}40` }}>
                  Review: {p.review_cadence}
                </span>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <UserCircle size={14} style={{ color: meta.accent }} />
                    <div className="text-[11px] uppercase tracking-wider text-[#A3ACA7]">Patient goals</div>
                  </div>
                  <ul className="space-y-1.5">
                    {p.patient_goals.map((g, i) => (
                      <li key={i} className="text-sm text-white flex items-start gap-2">
                        <CheckCircle2 size={13} className="mt-0.5 flex-shrink-0" style={{ color: meta.accent }} />
                        <span>{g}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <Target size={14} style={{ color: meta.accent }} />
                    <div className="text-[11px] uppercase tracking-wider text-[#A3ACA7]">Provider tasks</div>
                  </div>
                  <ul className="space-y-1.5">
                    {p.provider_tasks.map((t, i) => (
                      <li key={i} className="text-sm text-white flex items-start gap-2">
                        <CheckCircle2 size={13} className="mt-0.5 flex-shrink-0" style={{ color: meta.accent }} />
                        <span>{t}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
